import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { type ComponentProps, useMemo } from "react";
import { z } from "zod";
import {
  diagnosticsCatalogOptions,
  diagnosticsValueFlagsOptions,
} from "@/client/@tanstack/react-query.gen";
import { PageHeader } from "@/components/app/pageHeader";
import { MipEraScope } from "@/components/charts/mipEraBar";
import DiagnosticSummaryTable from "@/components/datasets/diagnosticSummaryTable.tsx";
import { DiagnosticCard } from "@/components/diagnostics/diagnosticCard";
import { DiagnosticsFilter } from "@/components/diagnostics/diagnosticsFilter";
import { ViewToggle } from "@/components/diagnostics/viewToggle";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LinkExternal } from "@/components/ui/link";
import AboutDiagnostics from "@/content/about-diagnostics.mdx";
import { useMipEra } from "@/hooks/useMipEra";
import {
  type CatalogDiagnostic,
  withValueFlags,
} from "@/lib/diagnosticCatalog";
import {
  type DiagnosticFilterState,
  filterDiagnostics,
  parseDiagnosticFilters,
  serialiseDiagnosticFilters,
} from "@/lib/diagnosticFilters";
import { mipEraSearchFields } from "@/lib/mipEras";

const diagnosticNotes: { slug: string; note: string; noteUrl?: string }[] = [
  {
    slug: "transient-climate-response-emissions",
    note: "Minimal available data",
  },
];

// Define search params schema for URL filtering
// Arrays are stored as comma-separated strings and parsed in the component
const diagnosticsSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  providers: z.string().optional().catch(undefined),
  aftIds: z.string().optional().catch(undefined),
  themes: z.string().optional().catch(undefined),
  metricValues: z.enum(["true", "false"]).optional().catch(undefined),
  view: z.enum(["cards", "table"]).default("cards"),
  ...mipEraSearchFields,
});

const CATALOG_HEADER = {
  title: "Diagnostics Catalog",
  description: (
    <div className="space-y-2">
      <p>
        A set of standardised diagnostics for evaluating climate model
        performance against observations and benchmarks.
      </p>
      <p>
        For the most up to date list and details of CMIP diagnostics please see
        the{" "}
        <LinkExternal href="https://doi.org/10.5281/zenodo.14284374">
          Zenodo record
        </LinkExternal>
        . For details on CMIP7 AFT diagnostic collection and reference dataset
        selection, please see{" "}
        <LinkExternal href="https://doi.org/10.5194/gmd-19-7415-2026">
          Rapid Evaluation Framework for the CMIP7 Assessment Fast Track
          (Hoffman et al., 2026)
        </LinkExternal>
        . To propose a new diagnostic, visit the{" "}
        <LinkExternal href="https://github.com/orgs/Climate-REF/discussions/categories/proposed-diagnostics">
          Climate-REF GitHub Discussion board
        </LinkExternal>
        .
      </p>
    </div>
  ),
};

/** Styles for the "About Diagnostics" prose, which sits in a card rather than a prose block */
const ABOUT_COMPONENTS = {
  h3: (props: ComponentProps<"h3">) => (
    <h3 className="font-semibold text-foreground pt-2 first:pt-0" {...props} />
  ),
  ul: (props: ComponentProps<"ul">) => (
    <ul className="list-disc pl-5 space-y-1" {...props} />
  ),
  hr: (props: ComponentProps<"hr">) => <hr className="my-4" {...props} />,
  a: (props: ComponentProps<"a">) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline font-medium"
    />
  ),
};

const ErrorComponent = ({ message }: { message: string }) => {
  return (
    <div className="container mx-auto p-4 space-y-6">
      <div>
        <PageHeader {...CATALOG_HEADER} />
      </div>

      <Alert variant="destructive">
        <AlertDescription>
          Failed to load diagnostics. Please try again later.
          <br />
          <span className="text-sm">{message}</span>
        </AlertDescription>
      </Alert>
    </div>
  );
};

/** The filtered diagnostics as cards or a table */
function DiagnosticResults({
  diagnostics,
  view,
  valueFlagsFailed,
}: {
  diagnostics: CatalogDiagnostic[];
  view: "cards" | "table";
  valueFlagsFailed: boolean;
}) {
  return (
    <>
      {view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {diagnostics.map((diagnostic) => {
            const note = diagnosticNotes.find(
              (n) => n.slug === diagnostic.slug,
            );
            return (
              <DiagnosticCard
                key={`${diagnostic.provider.slug}-${diagnostic.slug}`}
                diagnostic={diagnostic}
                valueFlagsFailed={valueFlagsFailed}
                note={note?.note}
                noteURL={note?.noteUrl}
              />
            );
          })}
        </div>
      ) : (
        <DiagnosticSummaryTable
          summaries={diagnostics}
          valueFlagsFailed={valueFlagsFailed}
        />
      )}

      {diagnostics.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No diagnostics found matching your filters.
          </p>
        </div>
      )}
    </>
  );
}

const Diagnostics = () => {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();
  const { mipEra, setMipEra } = useMipEra(searchParams.mip_era);
  // The value flags are the slow part of the listing, so they load separately and fill in once ready.
  const { data, isLoading, error } = useQuery(
    diagnosticsCatalogOptions({ query: { mip_era: mipEra } }),
  );
  const valueFlags = useQuery(
    diagnosticsValueFlagsOptions({ query: { mip_era: mipEra } }),
  );
  const diagnostics = useMemo(
    () => withValueFlags(data?.data ?? [], valueFlags.data?.data),
    [data, valueFlags.data],
  );
  const filters = useMemo(
    () => parseDiagnosticFilters(searchParams),
    [searchParams],
  );
  const filteredDiagnostics = useMemo(
    () => filterDiagnostics(diagnostics, filters),
    [diagnostics, filters],
  );

  const handleViewChange = (newView: "cards" | "table") => {
    navigate({
      search: (prev) => ({ ...prev, view: newView }),
      replace: true,
    });
  };

  const handleFilterChange = (next: DiagnosticFilterState) => {
    navigate({
      search: (prev) => ({ ...prev, ...serialiseDiagnosticFilters(next) }),
      replace: true,
    });
  };

  // Show error state if query fails
  if (error) {
    return (
      <ErrorComponent
        message={error instanceof Error ? error.message : String(error)}
      />
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <title>{`Diagnostics (${mipEra}) - Climate-REF`}</title>
      <div>
        <PageHeader
          {...CATALOG_HEADER}
          actions={
            <ViewToggle
              view={searchParams.view}
              onViewChange={handleViewChange}
            />
          }
        />
      </div>

      <Card>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <AboutDiagnostics components={ABOUT_COMPONENTS} />
        </CardContent>
      </Card>

      {valueFlags.isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            Could not load which diagnostics have metric values, so the metric
            values filter is not applied.
            <Button
              variant="outline"
              size="sm"
              onClick={() => valueFlags.refetch()}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <MipEraScope mipEra={mipEra} setMipEra={setMipEra}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span className="text-muted-foreground">
                Loading diagnostics...
              </span>
            </div>
          ) : (
            <DiagnosticsFilter
              diagnostics={diagnostics}
              value={filters}
              onChange={handleFilterChange}
            />
          )}
        </MipEraScope>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <Card
              // biome-ignore lint/suspicious/noArrayIndexKey: Placeholders have no identity
              key={`diagnostic-skeleton-${index}`}
              className="animate-pulse"
            >
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded mb-2" />
                <div className="h-3 bg-muted rounded mb-1 w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <DiagnosticResults
          diagnostics={filteredDiagnostics}
          view={searchParams.view}
          valueFlagsFailed={valueFlags.isError}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_app/diagnostics/")({
  component: Diagnostics,
  staticData: {
    title: "Diagnostics",
  },
  validateSearch: diagnosticsSearchSchema,
});
